import {Box} from "@mui/material";
import {MeshGradient} from "@paper-design/shaders-react";
import {useState} from "react";
import {colors} from "../../theme";
import {useInView} from "~/new-design/hooks";

type GradientBackgroundProps = {
  gradientColors?: string[];
  fadeToColor?: string;
};

const DEFAULT_COLORS = [colors.tertiary[100], colors.tertiary[100], "#f3edff", "#fdf9f1", "#f7eedf"];

const isStaticPreferred = () => {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia;
  if (!mql) return false;
  return (
    mql("(prefers-reduced-motion: reduce)").matches ||
    mql("(max-width: 600px)").matches ||
    mql("(pointer: coarse)").matches
  );
};

export const GradientBackground = ({
  gradientColors = DEFAULT_COLORS,
  fadeToColor = colors.tertiary[100],
}: GradientBackgroundProps) => {
  const [staticGradient] = useState(isStaticPreferred);
  const [rootRef, inView] = useInView<HTMLDivElement>();

  return (
    // Full-bleed fixed background uses the large viewport (lvh) so it still
    // covers the screen when mobile browser chrome collapses (no bottom gap).
    <Box ref={rootRef} sx={{position: "fixed", top: 0, left: 0, right: 0, height: "100lvh", zIndex: 0}}>
      {staticGradient ? (
        // Mobile / reduced-motion: skip WebGL entirely, show a static gradient.
        <Box
          sx={{width: "100%", height: "100%", background: `linear-gradient(180deg, ${gradientColors.join(", ")})`}}
        />
      ) : (
        // speed 0 when off-screen pauses the shader loop without tearing down the WebGL context.
        <MeshGradient
          colors={gradientColors}
          speed={inView ? 0.76 : 0}
          distortion={0.2}
          swirl={0.1}
          style={{width: "100%", height: "100%"}}
        />
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "30%",
          background: `linear-gradient(to bottom, transparent, ${fadeToColor})`,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};
